import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Client } from '../../../../../projects/shared-lib/src/lib/models/client';
import { environment } from '../../../../../environments/environment';
import { WebsiteSaleService } from '../../../../../projects/shared-lib/src/lib/services/website-sale.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
    MessageAction,
    MessageObserverService,
} from '../../../../../projects/shared-lib/src/lib/services/message-observer.service';
import { ClientService } from '../../../client/client.service';
import { Router } from '@angular/router';
import { ClientDetails } from '../../../../../projects/shared-lib/src/lib/models/client-details';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormErrorMessageComponent } from '../form/form-error-message/form-error-message.component';
import { EmailValidatorDirective } from '../../directive/email-validator.directive';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatButton } from '@angular/material/button';
import { PrefixEnum } from '../../../../../projects/shared-lib/src/lib/models/prefix.enum';

@Component({
    selector: 'tw-client-form',
    templateUrl: './client-form.component.html',
    styleUrls: ['./client-form.component.css'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        FormErrorMessageComponent,
        EmailValidatorDirective,
        MatSelect,
        MatOption,
        MatButton,
        TranslatePipe,
    ]
})
export class ClientFormComponent implements OnInit {
    @Input() formGroupClient:any| UntypedFormGroup ;
    @Input() client: Client;
    lang: String;
    isGuestForm: boolean;
    environment = environment;
    load = true;
    isCheckoutPage = false;
    prefixOptions = Object.values(PrefixEnum);

    private englishPrefixes = [
        PrefixEnum.MR,
        PrefixEnum.MRS,
        PrefixEnum.MX,
        PrefixEnum.NONE
    ];

    private frenchPrefixes = [
        PrefixEnum.M,
        PrefixEnum.MME,
        PrefixEnum.MX,
        PrefixEnum.AUCUN
    ];

    private getPrefixMapping(targetLang: string): Map<PrefixEnum, PrefixEnum> {
        if (targetLang === 'en') {
            return new Map([
                [PrefixEnum.M, PrefixEnum.MR],
                [PrefixEnum.MME, PrefixEnum.MRS],
                [PrefixEnum.MX, PrefixEnum.MX],
                [PrefixEnum.AUCUN, PrefixEnum.NONE],
                [PrefixEnum.MR, PrefixEnum.MR],
                [PrefixEnum.MRS, PrefixEnum.MRS],
                [PrefixEnum.NONE, PrefixEnum.NONE],
            ]);
        } else {
            return new Map([
                [PrefixEnum.MR, PrefixEnum.M],
                [PrefixEnum.MRS, PrefixEnum.MME],
                [PrefixEnum.MX, PrefixEnum.MX],
                [PrefixEnum.NONE, PrefixEnum.AUCUN],
                [PrefixEnum.M, PrefixEnum.M],
                [PrefixEnum.MME, PrefixEnum.MME],
                [PrefixEnum.AUCUN, PrefixEnum.AUCUN],
            ]);
        }
    }

    constructor(
        private fb: UntypedFormBuilder,
        private websiteSaleService: WebsiteSaleService,
        public translate: TranslateService,
        private clientService: ClientService,
        private messageObserverService: MessageObserverService,
        private router: Router,
    ) {
        this.isGuestForm =
            this.websiteSaleService.getLocalConfig().clientAccount === 'NO';
    }

    ngOnInit() {
        if(!this.formGroupClient){
            this.formGroupClient = new UntypedFormGroup({});
        }

        this.isCheckoutPage = this.router.url.search('mode') !== -1;

        this.formGroupClient.addControl(
            'firstname',
            this.fb.control(
                this.client && this.client.firstname
                    ? this.client.firstname
                    : '',
            ),
        );
        this.formGroupClient.addControl(
            'lastname',
            this.fb.control(
                this.client && this.client.lastname ? this.client.lastname : '',
            ),
        );
        this.formGroupClient.addControl(
            'email',
            this.fb.control(
                this.client && this.client.email ? this.client.email : '',
            ),
        );

        if (!this.isGuestForm) {
            this.formGroupClient.addControl(
                'language',
                this.fb.control(
                    this.client && this.client.language
                        ? this.client.language
                        : '',
                ),
            );
        }

        this.formGroupClient.addControl(
            'prefix',
            this.fb.control(
                this.client && this.client.prefix ? this.client.prefix : '',
            ),
        );

        if (environment.systemValues === 'c2') {
            this.formGroupClient.addControl(
                'company',
                this.fb.control(
                    this.client && this.client.company
                        ? this.client.company
                        : '',
                ),
            );
        }

        this.lang =
            this.client && this.client.language
                ? this.client.language
                : this.translate.currentLang;

        this.updatePrefixOptions();

        this.translate.onLangChange.subscribe(() => {
            this.updatePrefixOptions();
        });
    }

    private updatePrefixOptions() {
        const currentLang = this.translate.currentLang || 'fr';
        this.prefixOptions = currentLang === 'en' ? [...this.englishPrefixes] : [...this.frenchPrefixes];

        // translate prefix
        if (this.client && this.client.prefix && this.formGroupClient) {
            const clientPrefix = this.client.prefix as PrefixEnum;
            const currentLangOptions: PrefixEnum[] = currentLang === 'en' ? this.englishPrefixes : this.frenchPrefixes;

            // if client already has prefix in orpheus vente, translate.
            const isInCurrentLanguage = currentLangOptions.some(opt => opt === clientPrefix);
            if (!isInCurrentLanguage) {
                const prefixMapping = this.getPrefixMapping(currentLang);
                const translatedPrefix = prefixMapping.get(clientPrefix);
                const defaultPrefix = currentLang === 'en' ? PrefixEnum.NONE : PrefixEnum.AUCUN;
                if (this.formGroupClient.get('prefix')) {
                    this.formGroupClient.get('prefix')?.setValue(translatedPrefix || defaultPrefix);
                }
            }
        }
    }

    updateClient() {
        this.load = false;
        //console.log(this.clientService.getClientLocal())
        const client = <ClientDetails>(
            JSON.parse(JSON.stringify(this.clientService.getClientLocal()))
        );

        if (client) {
            client.firstname = this.formGroupClient.value.firstname;
            client.lastname = this.formGroupClient.value.lastname;
            client.prefix = this.formGroupClient.value.prefix;
        }
        this.clientService.updateClient(client).subscribe(
            () => {
                this.load = true;
                this.messageObserverService.addMessage(<MessageAction>{
                    dialog: false,
                    message: 'success.update',
                    translate: true,
                    snackBar: {},
                });
            },
            (error1) => {
                this.load = true;
                this.messageObserverService.addMessage(<MessageAction>{
                    dialog: true,
                    message: error1.error.error.message,
                });
            },
        );
    }
}
